import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'

/**
 * Scroll-Layer — bewusst NATIV. Lenis + ScrollTrigger.normalizeScroll wurden
 * entfernt: beide fingen das Wheel-Event ab und erzeugten das „schwimmende",
 * laggy Scrollgefühl; ihr einziger Zweck (Anti-Bounce bei gepinnten
 * Scrub-Sections) ist entfallen, seit keine Section mehr pinnt.
 *
 *  - ScrollTrigger läuft direkt auf dem nativen Scroll (kein Proxy, kein Drift).
 *  - Anker-Navigation animiert über ScrollToPlugin (kurz, power3.out) — bei
 *    reduced-motion springt sie sofort.
 *  - ScrollTrigger.refresh() nach Fonts-Load und window.load, damit
 *    Trigger-Positionen erst nach finalem Layout eingefroren werden — und
 *    nach jedem Seitenwechsel: Nav und Footer bleiben stehen, ihre Trigger
 *    wären sonst auf die Höhe der vorigen Seite vermessen.
 */
export function useSmoothScroll() {
  const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  let offPageFinish: (() => void) | undefined

  function init() {
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)

    document.fonts?.ready.then(() => ScrollTrigger.refresh()).catch(() => undefined)
    if (document.readyState === 'complete') {
      ScrollTrigger.refresh()
    } else {
      window.addEventListener('load', () => ScrollTrigger.refresh(), { once: true })
    }
    offPageFinish = useNuxtApp().hook('page:finish', () => {
      requestAnimationFrame(() => ScrollTrigger.refresh())
    })
  }

  function destroy() {
    offPageFinish?.()
  }

  /** Anker-Navigation: weich via ScrollToPlugin, bei reduced-motion sofort.
   *  Danach bekommt die Ziel-Section den Fokus, damit Tastatur-Nutzer dort
   *  weitertabben (und nicht in der Nav hängen bleiben). */
  function scrollToTarget(selector: string) {
    const target = selector === 'body' ? null : document.querySelector<HTMLElement>(selector)
    if (selector !== 'body' && !target) return

    const focusTarget = () => {
      if (!target) return
      target.tabIndex = -1
      target.focus({ preventScroll: true })
    }

    if (prefersReducedMotion()) {
      if (target) target.scrollIntoView({ block: 'start' }) // scroll-margin-top (main.css) hält den Nav-Abstand
      else window.scrollTo(0, 0)
      focusTarget()
      return
    }
    gsap.to(window, {
      scrollTo: target ? { y: target, offsetY: 96 } : 0,
      duration: 0.9,
      ease: 'power3.out',
      overwrite: 'auto',
      onComplete: focusTarget
    })
  }

  return { init, destroy, scrollToTarget, prefersReducedMotion }
}
